import { PageContainer } from "@client/components/ui";
import { Button } from "@client/components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@client/components/ui/card";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

const verifyEmailSearchSchema = z.object({
	token: z.string().optional(),
	error: z.string().optional(),
});

export const Route = createFileRoute("/(auth)/verify-email")({
	component: VerifyEmailPage,
	validateSearch: verifyEmailSearchSchema,
});

function VerifyEmailPage() {
	const navigate = useNavigate();
	const search = useSearch({ from: "/(auth)/verify-email" });
	const [status, setStatus] = useState<"loading" | "success" | "error">(
		"loading",
	);
	const [errorMessage, setErrorMessage] = useState<string>("");

	const verifyToken = useMemo(() => search.token, [search.token]);
	const urlError = useMemo(() => search.error, [search.error]);

	useEffect(() => {
		// Check for URL error first
		if (urlError === "invalid_token") {
			setStatus("error");
			setErrorMessage("The verification link is invalid or has expired.");
			return;
		}

		if (!verifyToken) {
			setStatus("error");
			setErrorMessage("No verification token provided.");
			return;
		}

		const verifyEmail = async () => {
			try {
				// Call the verify email endpoint directly
				const response = await fetch(
					`/api/auth/verify-email?token=${encodeURIComponent(verifyToken)}`,
					{
						method: "GET",
					},
				);

				const result = (await response.json()) as {
					error?: boolean;
					message?: string;
				};

				if (!response.ok || result.error) {
					setStatus("error");
					setErrorMessage(result.message || "Failed to verify email");
				} else {
					setStatus("success");
				}
			} catch (error) {
				setStatus("error");
				setErrorMessage(
					error instanceof Error
						? error.message
						: "An unexpected error occurred",
				);
			}
		};

		verifyEmail();
	}, [verifyToken, urlError]);

	if (status === "loading") {
		return (
			<PageContainer maxWidth="narrow" className="py-10">
				<Card className="border-2 dark:border-gray-700">
					<CardHeader className="text-center">
						<CardTitle className="font-bold text-3xl dark:text-gray-100">
							Verifying Email...
						</CardTitle>
					</CardHeader>
					<CardContent className="flex justify-center py-8">
						<Loader2 className="h-8 w-8 animate-spin text-primary" />
					</CardContent>
				</Card>
			</PageContainer>
		);
	}

	if (status === "error") {
		return (
			<PageContainer maxWidth="narrow" className="py-10">
				<Card className="border-2 dark:border-gray-700">
					<CardHeader className="text-center">
						<CardTitle className="font-bold text-3xl dark:text-gray-100">
							Verification Failed ❌
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="rounded-lg bg-red-50 p-4 text-center dark:bg-red-900/20">
							<p className="text-red-800 dark:text-red-200">{errorMessage}</p>
							<p className="mt-2 text-red-700 text-sm dark:text-red-300">
								The verification link may have expired or already been used.
							</p>
						</div>
						<div className="flex flex-col gap-2">
							<Button className="w-full" onClick={() => navigate({ to: "/" })}>
								Go to Sign In
							</Button>
							<Button
								variant="outline"
								className="w-full"
								onClick={() => navigate({ to: "/" })}
							>
								Go to Home
							</Button>
						</div>
					</CardContent>
				</Card>
			</PageContainer>
		);
	}

	return (
		<PageContainer maxWidth="narrow" className="py-10">
			<Card className="border-2 dark:border-gray-700">
				<CardHeader className="text-center">
					<CardTitle className="font-bold text-3xl dark:text-gray-100">
						Email Verified! ✅
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="rounded-lg bg-green-50 p-4 text-center dark:bg-green-900/20">
						<p className="text-green-800 dark:text-green-200">
							Your email has been successfully verified.
						</p>
						<p className="mt-2 text-green-700 text-sm dark:text-green-300">
							You now have full access to all features.
						</p>
					</div>
					<Button
						className="w-full"
						onClick={() => navigate({ to: "/dashboard" })}
					>
						Go to Dashboard
					</Button>
				</CardContent>
			</Card>
		</PageContainer>
	);
}
